import { httpResponseStatusCodes } from '@/constants/http-response-status-codes';
import { regexes } from '@/constants/regexes';
import { PagedListOf } from '@/types/paged-requests/paged-request';
import { signOut } from 'next-auth/react';

export type JsonReviverType = {
  jsonParseReviver?: (key: string, value: unknown) => unknown;
};

export type CommonFetchResponse<T = unknown> = {
  data: T;
  headers?: Headers | null | undefined;
};

export const createCommonDefaultPagedData = <T = unknown>(): CommonFetchResponse<PagedListOf<T>> => ({
  data: {
    data: [] as T[],
    hasNextPage: false,
    hasPreviousPage: false,
    pageCount: 0,
    pageNumber: 1,
    pageSize: 10,
    totalItemCount: 0,
  },
  headers: null,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const jsonParseReviver = (key: string, value: any): any => {
  if (typeof value === 'string') {
    if (regexes.iso8601.test(value)) {
      return new Date(value);
    }
  }

  return value;
};

export const commonFetch = async <T = unknown>(
  input: string | URL | globalThis.Request,
  init?: RequestInit
): Promise<CommonFetchResponse<T>> => {
  const response = await fetch(input, init);

  if (response.ok) {
    try {
      const textContent = await response.text();
      const responseObject = !textContent ? {} : JSON.parse(textContent, jsonParseReviver);

      return { data: responseObject as T, headers: response.headers };
    } catch {
      return { data: null as T, headers: null };
    }
  } else if (response.status === httpResponseStatusCodes.notFound) {
    return { data: null as T, headers: null };
  } else if (response.status === httpResponseStatusCodes.notAuthorized) {
    signOut();
    throw new Error('Not authorized');
  }

  const responseBody = await response.text();

  try {
    const errorResponse = !responseBody ? { message: response.statusText } : JSON.parse(responseBody, jsonParseReviver);
    if (errorResponse.message === 'Not authorized') {
      signOut();
    }
    if (errorResponse.message || errorResponse.swaggerMessage) {
      throw new Error(errorResponse.message || errorResponse.swaggerMessage);
    }
  } catch (error: unknown) {
    if ((error as { message: string; }).message.indexOf('Unexpected token') === -1) {
      throw error;
    }
  }

  throw new Error('Failed to fetch');
};
